import { Reducer, useEffect, useReducer } from "react"

import { useAuth } from "./auth"
import { Notification } from "../messaging/data-types"
import { generalNotifications, messagesNotifications, notificationsCount } from "../messaging/notifications"


type NotificationsState = {
    pending: boolean
    final: boolean
    notifications?: Array<Notification>
    error?: string
}

type UpdateFunction = (offset: number, limit: number) => Promise<void>

type NotificationsCountState = {
    messages: number
    notifications: number
}

export const useGeneralNotifications = (): [NotificationsState, UpdateFunction] => {
    const user = useAuth()

    const [state, setState] = useReducer<Reducer<NotificationsState, Partial<NotificationsState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: false, 
            final: true,
            notifications: [],
        }
    )

    const update = async (offset: number, limit: number) => {
        if (!user.token) {
            return
        }

        const startState: Partial<NotificationsState> = { pending: true, final: false }

        setState(startState)
        try {
            const { data, error } = await generalNotifications(offset, limit, user.token?.access)
            if (error != null) {
                setState({ error: error, pending: false })
                return
            }

            if (!data?.length) {
                setState({ final: true, pending: false })
                return
            }

            setState({ notifications: [ ...(state.notifications || []), ...data], pending: false })
        } catch (e) {
            setState({ error: e as string, pending: false })
        }
    }

    return [state, update]
}


export const useMessagesNotifications = (): [NotificationsState, UpdateFunction] => {
    const user = useAuth()

    const [state, setState] = useReducer<Reducer<NotificationsState, Partial<NotificationsState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: false, 
            final: true,
            notifications: [],
        }
    )

    const update = async (offset: number, limit: number) => {
        if (!user.token) {
            return
        }

        const startState: Partial<NotificationsState> = { pending: true, final: false }

        setState(startState)
        try {
            const { data, error } = await messagesNotifications(offset, limit, user.token?.access)
            if (error != null) {
                setState({ error: error, pending: false })
                return
            }

            if (!data?.length) {
                setState({ final: true, pending: false })
                return
            }

            setState({ notifications: [ ...(state.notifications || []), ...data], pending: false })
        } catch (e) {
            setState({ error: e as string, pending: false })
        }
    }

    return [state, update]
}

export const useNotificationsCount = (): NotificationsCountState => {
    const user = useAuth()

    const [state, setState] = useReducer<Reducer<NotificationsCountState, Partial<NotificationsCountState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            notifications: 0,
            messages: 0,
        }
    )

    useEffect(() => {
        if (!user.token?.access) {
            return
        }

        const intervalFunc = async () => {
            const { error, data } = await notificationsCount(user.token?.access!)

            if (error) {
                console.log({ error })
                return
            }

            if (!data) {
                console.log("Data is empty")
                return
            }

            setState({
                messages: data!.messages_cafe + data!.messages_salon,
                notifications: data!.various,
            })
        }

        intervalFunc()
        const bg = setInterval(intervalFunc, 2000)

        return () => {
            console.log("Unsubscribing from counts")
            clearInterval(bg)
        }
    }, [user.token?.access])

    return state
}